import type { DropdownListItem } from '@verantos-dev/shared-components';
import { type ConceptGroup } from '@verantos-dev/types-concepts';

export type CohortBuilderDslLegacy = {
  /**
   * The full cohort dsl string as defined.
   */
  fullCohortDsl: string;

  /**
   * The cohort dsl strings split up by section.
   */
  cohortDsl: CohortDslLegacy;
};

export type CohortBuilderModelLegacy = {
  /**
   * Array of subject variables.
   */
  simpleEvents: SimpleEventLegacy[];

  /**
   * Array of derived event variables.
   */
  derivedEvents: DerivedEventLegacy[];

  /**
   * The index date variable.
   */
  indexDate: IndexDateVariableLegacy | undefined;

  /**
   * Array of subject variables.
   */
  subjectVariables: SubjectVariableLegacy[];

  /**
   * Study period restrictions.
   */
  studyPeriodRestrictions: StudyPeriodRestrictionsLegacy | undefined;
};

export type CohortBuilderDefinitionLegacy = CohortBuilderModelLegacy & CohortBuilderDslLegacy;

export type CohortDslLegacy = {
  inclusionCriteria: DSLValueLegacy[];
  exclusionCriteria: DSLValueLegacy[];
  studyPeriodRestrictions: DSLValueLegacy[];
  simpleEvents: DSLValueLegacy[];
  derivedEvents: DSLValueLegacy[];
  indexDate: DSLValueLegacy | undefined;
  subjectVariables: DSLValueLegacy[];
};

export type DSLValueLegacy = {
  id: string;
  value: string;
};

export type StudyPeriodRestrictionsLegacy = {
  overallStudyPeriodRestriction: OverallStudyPeriodRestrictionLegacy | undefined;
  baselinePeriodRestriction: BaselinePeriodRestrictionLegacy | undefined;
  followUpPeriodRestriction: FollowUpPeriodRestrictionLegacy | undefined;
  changeStatus?: CohortBuilderItemChangeStatusLegacy | undefined;
};

export type OverallStudyPeriodRestrictionLegacy = {
  isEnabled?: boolean;
  startDate: string;
  endDate: string;
};

export type BaselinePeriodRestrictionLegacy = {
  isEnabled?: boolean;
  daysBeforeIndexDate: number | undefined;
};

export type FollowUpPeriodRestrictionLegacy = {
  isEnabled?: boolean;
  daysAfterIndexDate: number | undefined;
};

export type IndexDateRangeLegacy = {
  startDate?: string;
  endDate?: string;
};

export type IndexDateVariableLegacy = {
  id: string;
  type: IndexDateTypeLegacy;
  indexDateValue: string;
  indexDateRangeValue?: IndexDateRangeLegacy;
  changeStatus?: CohortBuilderItemChangeStatusLegacy;
};

export type SubjectVariableLegacy = {
  id: string;
  name: string;
  type: SubjectVariableTypeLegacy | undefined;
  conceptFilters: FilterLegacy[];
  numericFilters: FilterLegacy[];
  countFilter: FilterLegacy | undefined;
  criteriaSelection: SubjectVariableCriteriaSelectionTypeLegacy;
  changeStatus?: CohortBuilderItemChangeStatusLegacy;
};

export type SimpleEventLegacy = {
  id: string;
  name: string;
  type: SimpleEventTypeLegacy | undefined;
  conceptFilters: FilterLegacy[];
  numericFilters: FilterLegacy[];
  relatedEntityFilters: FilterLegacy[];
  uniquenessFilters: FilterLegacy[];
  changeStatus?: CohortBuilderItemChangeStatusLegacy;
};

export type DerivedEventLegacy = {
  id: string;
  name: string;
  type: DerivedEventTypeLegacy | undefined;
  anyOrder?: boolean;
  temporalConstraints: FilterLegacy[];
  eventFilters: DerivedEventFilterLegacy[];
  uniquenessFilters: FilterLegacy[];
  changeStatus?: CohortBuilderItemChangeStatusLegacy;
};

export type CohortBuilderEventLegacy = {
  eventName: InputFieldLegacy;
  eventDate: InputFieldLegacy;
  selected?: boolean;
};

export type FilterLegacy = {
  id: string;
  inputFields: InputFieldLegacy[];
};

export type InputFieldLegacy = {
  id: string;
  value: string | undefined;
  dirty?: boolean; // annotation indicating input field has been touched
};

export type InputFieldConfig = {
  inputType: InputFieldTypeLegacy;
  width: 'xxlarge' | 'xlarge' | 'large' | 'medium' | 'small' | 'dialog' | 'auto' | number;
  label: string;
  options?: DropdownListItem[];
  placeholder?: string;
  helperText?: string;
  minValue?: number;
  minValueMsg?: string;
  maxValue?: number;
  maxValueMsg?: string;
  step?: number;
  disabled?: boolean;
};

export type DerivedEventFilterLegacy = {
  id: string; // id of this event filter occurrence
  eventName: InputFieldLegacy; // name of the event that is selected
  eventDateAttribute: InputFieldLegacy;
  isSelected?: InputFieldLegacy; // only for sequence events
  position?: InputFieldLegacy; // only for instance events
  index?: InputFieldLegacy; // only for instance events
};

export enum CohortBuilderItemChangeStatusLegacy {
  NEW = 'NEW',
  EDITED = 'EDITED',
  UNCHANGED = 'UNCHANGED',
}

export enum DerivedEventTypeLegacy {
  MERGED = 'Merged',
  SEQUENCE = 'Sequence',
  INSTANCE = 'Instance',
}

export enum SimpleEventTypeLegacy {
  CONDITION_OCCURRENCE = 'Condition occurrence',
  DRUG_EXPOSURE = 'Drug exposure',
  MEASUREMENT = 'Measurement',
  OBSERVATION = 'Observation',
  PROCEDURE_OCCURRENCE = 'Procedure occurrence',
  VISIT_OCCURRENCE = 'Visit occurrence',
}

export enum SubjectVariableTypeLegacy {
  DEMOGRAPHICS = 'Demographics',
  COUNT = 'Count',
}

export enum SubjectVariableCriteriaSelectionTypeLegacy {
  INCLUSION = 'Inclusion',
  EXCLUSION = 'Exclusion',
  NOT_APPLIED = 'Not applied',
}

export enum IndexDateTypeLegacy {
  EVENT = 'Event instance',
  CALENDAR = 'Calendar date',
}

export enum UniquenessFilterTypesEnumLegacy {
  FIXED_WINDOW = 'FIXED_WINDOW',
  ROLLING_WINDOW = 'ROLLING_WINDOW',
}

export enum UniquenessFilterTimeUnitEnumLegacy {
  DAYS = 'DAYS',
}

export enum UniquenessFilterDateAttributesEnumLegacy {
  DERIVED_EVENT_DATE = 'derived_event_date',
}

export enum CohortBuilderStepLegacy {
  EVENTS = 'Events',
  VARIABLES = 'Variables',
  DEFINITION = 'Definition',
  SUBJECTSV2 = 'SubjectsV2',
  CHARACTERISTICS = 'Characteristics',
  SUMMARY = 'Summary',
}

export enum CohortBuilderModalLegacy {
  CONFIGURE_STUDY_PERIOD,
  ADD_INDEX_DATE,
  EDIT_INDEX_DATE,
  ADD_SUBJECT_VARIABLE,
  EDIT_SUBJECT_VARIABLE,
  ADD_SIMPLE_EVENT,
  EDIT_SIMPLE_EVENT,
  ADD_DERIVED_EVENT,
  EDIT_DERIVED_EVENT,
}

export enum CohortActionModalLegacy {
  EXIT_COHORT,
  SAVE_COHORT,
  PROCESS_COHORT,
}

export enum FilterTypeLegacy {
  CONCEPT,
  NUMERIC,
  UNIQUENESS,
  RELATED_ENTITY,
  TEMPORAL,
  COUNT,
  EVENT,
}

export enum InputFieldTypeLegacy {
  COMBO_BOX = 'COMBO_BOX',
  CONCEPT_COMBO_BOX = 'CONCEPT_COMBO_BOX',
  DATE_PICKER = 'DATE_PICKER',
  DROPDOWN = 'DROPDOWN',
  FLOAT_NUMERIC_FIELD = 'FLOAT_NUMERIC_FIELD',
  INT_NUMERIC_FIELD = 'INT_NUMERIC_FIELD',
  TEXT_FIELD = 'TEXT_FIELD',
  CHECKBOX = 'CHECKBOX',
}

export enum DerivedEventInputFieldTypeLegacy {
  EVENT_NAME,
  EVENT_DATE_ATTRIBUTE,
  EVENT_IS_SELECTED,
  EVENT_POSITION,
  EVENT_INDEX,
}

export enum ConceptAttributesEnumLegacy {
  GENDER_CONCEPT = 'gender_concept',
  RACE_CONCEPT = 'race_concept',
  ETHNICITY_CONCEPT = 'ethnicity_concept',
}

export enum ComparatorOperatorsEnumLegacy {
  IN = 'IN',
  EQUAL = '=',
  NOT_EQUAL = '!=',
  SMALLER_THAN = '<',
  GREATER_THAN = '>',
  SMALLER_OR_EQUAL = '<=',
  GREATER_OR_EQUAL = '>=',
}

export enum NumericFiltersAttributesEnumLegacy {
  YEAR_OF_BIRTH = 'year_of_birth',
  MONTH_OF_BIRTH = 'month_of_birth',
  DAY_OF_BIRTH = 'day_of_birth',
}

export enum ConceptAttributesConditionOccurrenceEnumLegacy {
  CONDITION_CONCEPT = 'condition_concept',
  CONDITION_TYPE_CONCEPT = 'condition_type_concept',
  CONDITION_SOURCE_CONCEPT = 'condition_source_concept',
  CONDITION_STATUS_CONCEPT = 'condition_status_concept',
}

export enum ConceptAttributesDrugExposureEnumLegacy {
  DRUG_CONCEPT = 'drug_concept',
  DRUG_TYPE_CONCEPT = 'drug_type_concept',
  ROUTE_CONCEPT = 'route_concept',
  DRUG_SOURCE_CONCEPT = 'drug_source_concept',
}

export enum ConceptAttributesMeasurementEnumLegacy {
  MEASUREMENT_CONCEPT = 'measurement_concept',
  MEASUREMENT_TYPE_CONCEPT = 'measurement_type_concept',
  OPERATOR_CONCEPT = 'operator_concept',
  VALUE_AS_NUMBER = 'value_as_number',
  VALUE_AS_CONCEPT = 'value_as_concept',
  UNIT_CONCEPT = 'unit_concept',
}

export enum ConceptAttributesObservationEnumLegacy {
  OBSERVATION_CONCEPT = 'observation_concept',
  OBSERVATION_TYPE_CONCEPT = 'observation_type_concept',
  VALUE_AS_NUMBER = 'value_as_number',
  VALUE_AS_CONCEPT = 'value_as_concept',
  UNIT_CONCEPT = 'unit_concept',
}

export enum ConceptAttributesProcedureOccurrenceEnumLegacy {
  PROCEDURE_CONCEPT = 'procedure_concept',
  PROCEDURE_TYPE_CONCEPT = 'procedure_type_concept',
  QUALIFIER_CONCEPT = 'qualifier_concept',
}

export enum ConceptAttributesVisitOccurrenceEnumLegacy {
  VISIT_CONCEPT = 'visit_concept',
  VISIT_TYPE_CONCEPT = 'visit_type_concept',
  VISIT_SOURCE_CONCEPT = 'visit_source_concept',
}

export enum NumericFiltersAttributesDrugExposureEnumLegacy {
  DAYS_SUPPLY = 'days_supply',
  REFILLS = 'refills',
  QUANTITY = 'quantity',
}

export enum NumericFiltersAttributesMeasurementEnumLegacy {
  RANGE_LOW = 'range_low',
  RANGE_HIGH = 'range_high',
  VALUE_AS_NUMBER = 'value_as_number',
}

export enum NumericFiltersAttributesObservationEnumLegacy {
  VALUE_AS_NUMBER = 'value_as_number',
}

export enum NumericFiltersAttributesProcedureOccurrenceEnumLegacy {
  QUANTITY = 'quantity',
}

// below are sort for each event type and filter

// CO = Concept Occurrence
// DE = Drug Exposure
// ME = Measurement
// OB = Observation
// PO = Procedure Occurrence
// VO = Visit Occurrence

// UF = Uniqueness Filter

export enum CO_UF_DateAttributesEnumLegacy {
  CONDITION_START_DATE = 'condition_start_date',
  CONDITION_END_DATE = 'condition_end_date',
  VISIT_START_DATE = 'visit_start_date',
  VISIT_END_DATE = 'visit_end_date',
}

export enum DE_UF_DateAttributesEnumLegacy {
  DRUG_EXPOSURE_START_DATE = 'drug_exposure_start_date',
  DRUG_EXPOSURE_END_DATE = 'drug_exposure_end_date',
}
export enum ME_UF_DateAttributesEnumLegacy {
  MEASUREMENT_DATE = 'measurement_date',
}

export enum OB_UF_DateAttributesEnumLegacy {
  OBSERVATION_DATE = 'observation_date',
}
export enum PO_UF_DateAttributesEnumLegacy {
  PROCEDURE_DATE = 'procedure_date',
}

export enum VO_UF_DateAttributesEnumLegacy {
  VISIT_START_DATE = 'visit_start_date',
  VISIT_END_DATE = 'visit_end_date',
}

// RE = Related Entity Filter

export enum RE_EntityFilterEnumLegacy {
  VISIT_OCCURRENCE = 'VISIT_OCCURRENCE',
  CONDITION_OCCURRENCE = 'CONDITION_OCCURRENCE',
  PROVIDER = 'PROVIDER',
}
export enum RE_AttributesEnumLegacy {
  VISIT_CONCEPT = 'visit_concept',
  VISIT_TYPE_CONCEPT = 'visit_type_concept',
  VISIT_SOURCE_CONCEPT = 'visit_source_concept',
  SPECIALTY_CONCEPT = 'specialty_concept',
  CONDITION_CONCEPT = 'condition_concept',
  CONDITION_TYPE_CONCEPT = 'condition_type_concept',
  CONDITION_STATUS_CONCEPT = 'condition_status_concept',
  CONDITION_SOURCE_CONCEPT = 'condition_source_concept',
}

export enum TemporalConstraintsEnumLegacy {
  MINIMUM_BETWEEN = 'MINIMUM_BETWEEN',
  DAYS = 'DAYS',
}

export enum InstanceEventEnumLegacy {
  CONDITION_START_DATE = 'condition_start_date',
}

export enum ResearchAssistantMessageEnumLegacy {
  INFO = 'info',
  SAVE = 'save',
}

export type InputOptionContextDataLegacy = {
  conceptGroups?: ConceptGroup[];
  eventNames?: DropdownListItem[];
  eventFilterNames?: string[];
  nextDerivedEventFilters?: DerivedEventFilterLegacy[];
  currentCohortDefinition?: CohortBuilderDefinitionLegacy;
  activeCohortBuilderItem?: ActiveCohortBuilderItemLegacy;
  derivedEventType?: DerivedEventTypeLegacy;
};

export type ActiveCohortBuilderItemLegacy =
  | ActiveSubjectsVariableItemLegacy
  | ActiveSimpleEventItemLegacy
  | ActiveDerivedEventItemLegacy
  | ActiveIndexDateItemLegacy;

export type ActiveIndexDateItemLegacy = {
  type: IndexDateTypeLegacy | undefined;
  indexDateValue: string | undefined;
  indexDateRangeValue?: IndexDateRangeLegacy;
} & CohortBuilderItemBase;

export type ActiveSubjectsVariableItemLegacy = {
  type: SubjectVariableTypeLegacy | undefined;
} & CohortBuilderItemBase;

export type ActiveSimpleEventItemLegacy = {
  type: SimpleEventTypeLegacy | undefined;
} & CohortBuilderItemBase;

export type ActiveDerivedEventItemLegacy = {
  type: DerivedEventTypeLegacy | undefined;
} & CohortBuilderItemBase;

type CohortBuilderItemBase = {
  id: string;
  name: string;
  anyOrder?: boolean;
  conceptFilters: FilterLegacy[];
  numericFilters: FilterLegacy[];
  relatedEntityFilters?: FilterLegacy[];
  uniquenessFilters?: FilterLegacy[];
  temporalConstraints: FilterLegacy[];
  eventFilters: DerivedEventFilterLegacy[];
  countFilter: FilterLegacy | undefined;
  inputFieldConfigMap: Record<string, InputFieldConfig>;
};

export enum GrammarDerivedEventTypeLegacy {
  MERGED = 'MERGED_EVENT',
  SEQUENCE = 'SEQUENCE_EVENT',
  INSTANCE = 'INSTANCE_EVENT',
}

export enum GrammarSimpleEventTypeLegacy {
  CONDITION_OCCURRENCE = 'CONDITION_OCCURRENCE',
  DRUG_EXPOSURE = 'DRUG_EXPOSURE',
  MEASUREMENT = 'MEASUREMENT',
  OBSERVATION = 'OBSERVATION',
  PROCEDURE_OCCURRENCE = 'PROCEDURE_OCCURRENCE',
  VISIT_OCCURRENCE = 'VISIT_OCCURRENCE',
}
